import { Fragment, useState } from "react";
import { useI18n } from "../lib/i18n";
import { MaterialLink } from "./MaterialPreview";
import { buildHistory, type AuditRow, type JobRow, type UserLike } from "../lib/history";
import { ROLE_LABEL, type MaterialChanges, type Role } from "../lib/types";
import { formatWeights, type ScoreWeights } from "../lib/score-weights";

/**
 * 実行履歴。**操作内容と操作者を必ず出す。**
 *
 * 「マニュアルが変わったが誰がやったのか分からない」状態を作らないための画面。
 * 機械の実行（jobs）と人の操作（audit_logs）は別の場所に残るので、
 * lib/history.ts で1本の時系列にまとめてから並べる。
 *
 * 行を開くと変更の中身が出る。素材の差し替えは**外したファイルも消していない**ので、
 * 当時の素材をそのまま開いて確かめられる。
 */

function when(d: Date | null, locale: string): string {
  return d ? d.toLocaleString(locale) : "—";
}

/** 素材の一覧。パスは長いので、名前をリンクにして押せば中身が出るようにする */
function Files({
  dishId,
  paths,
  onOpen,
}: {
  dishId: string;
  paths: string[];
  onOpen: (p: string) => void;
}) {
  if (paths.length === 0) return <span className="muted">—</span>;
  return (
    <ul className="files">
      {paths.map((p) => (
        <li key={p}>
          <MaterialLink dishId={dishId} path={p} onOpen={onOpen} />
        </li>
      ))}
    </ul>
  );
}

function MaterialDiff({
  dishId,
  changes,
  onOpen,
  t,
}: {
  dishId: string;
  changes: MaterialChanges;
  onOpen: (p: string) => void;
  t: (ja: string, params?: Record<string, string | number>) => string;
}) {
  return (
    <dl className="kv">
      {changes.addedVideos.length > 0 && (
        <>
          <dt>{t("追加した動画")}</dt>
          <dd><Files dishId={dishId} paths={changes.addedVideos} onOpen={onOpen} /></dd>
        </>
      )}
      {changes.removedVideos.length > 0 && (
        <>
          <dt>{t("外した動画")}</dt>
          <dd><Files dishId={dishId} paths={changes.removedVideos} onOpen={onOpen} /></dd>
        </>
      )}
      {changes.reorderedVideos && (
        <>
          <dt>{t("動画の順序")}</dt>
          {/* 順序は結合順の正。ファイルが同じでも工程の順番が変わる */}
          <dd>{t("結合する順序を並べ替えました。")}</dd>
        </>
      )}
      {changes.addedDocs.length > 0 && (
        <>
          <dt>{t("追加した資料")}</dt>
          <dd><Files dishId={dishId} paths={changes.addedDocs} onOpen={onOpen} /></dd>
        </>
      )}
      {changes.removedDocs.length > 0 && (
        <>
          <dt>{t("外した資料")}</dt>
          <dd><Files dishId={dishId} paths={changes.removedDocs} onOpen={onOpen} /></dd>
        </>
      )}
    </dl>
  );
}

export function History({
  dishId,
  jobs,
  audits,
  users,
  onOpenMaterial,
}: {
  dishId: string;
  jobs: JobRow[];
  audits: AuditRow[];
  users: Record<string, UserLike>;
  onOpenMaterial: (path: string) => void;
}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState<string | null>(null);
  const rows = buildHistory(jobs, audits, users);
  if (rows.length === 0) return null;

  return (
    <div className="card">
      <h2>{t("実行履歴")}</h2>
      <p className="muted small">
        {t("いつ・誰が・何をしたかの記録です。行を押すと変更の中身を確認できます。")}
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>{t("日時")}</th>
            <th>{t("操作内容")}</th>
            <th>{t("操作者")}</th>
            <th>{t("状態")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const expanded = open === r.id;
            const audit = r.audit;
            const job = r.job;
            return (
              <Fragment key={r.id}>
                <tr className="row-click" onClick={() => setOpen(expanded ? null : r.id)}>
                  <td className="muted small" data-label={t("日時")}>{when(r.at, locale)}</td>
                  <td data-label={t("操作内容")}>
                    {t(r.action)}
                    {r.kind === "audit" && <span className="badge sub">{t("操作")}</span>}
                  </td>
                  <td className="small" data-label={t("操作者")}>
                    {r.actor}
                    {audit?.actorRole && (
                      <span className="muted"> ／ {t(ROLE_LABEL[audit.actorRole as Role] ?? audit.actorRole)}</span>
                    )}
                  </td>
                  <td data-label={t("状態")}>
                    {r.statusLabel ? (
                      <span className={`badge ${r.status}`}>{t(r.statusLabel)}</span>
                    ) : (
                      <span className="muted small">{t("完了")}</span>
                    )}
                  </td>
                  <td className="muted small toggle-cell">{expanded ? "▲" : "▼"}</td>
                </tr>
                {expanded && (
                  <tr className="row-detail">
                    <td colSpan={5}>
                      {audit && (
                        <>
                          {audit.reason && (
                            <p className="small">
                              <strong>{t("理由:")}</strong> {audit.reason}
                            </p>
                          )}
                          {audit.action === "score_weights_changed" ? (
                            /* 採点の重みの変更。null は既定の重み */
                            <dl className="kv small">
                              <dt>{t("変更前の重み")}</dt>
                              <dd>{formatWeights(audit.beforeWeights as ScoreWeights | null, t)}</dd>
                              <dt>{t("変更後の重み")}</dt>
                              <dd>{formatWeights(audit.afterWeights as ScoreWeights | null, t)}</dd>
                            </dl>
                          ) : audit.beforeName && audit.afterName ? (
                            /* 料理名の変更。素材の差分とは別の形なので個別に出す */
                            <dl className="kv small">
                              <dt>{t("変更前の料理名")}</dt>
                              <dd>
                                {audit.beforeName.name}
                                {audit.beforeName.nameJa && (
                                  <span className="muted"> / {audit.beforeName.nameJa}</span>
                                )}
                              </dd>
                              <dt>{t("変更後の料理名")}</dt>
                              <dd>
                                {audit.afterName.name}
                                {audit.afterName.nameJa && (
                                  <span className="muted"> / {audit.afterName.nameJa}</span>
                                )}
                              </dd>
                            </dl>
                          ) : audit.changes ? (
                            <MaterialDiff dishId={dishId} changes={audit.changes} onOpen={onOpenMaterial} t={t} />
                          ) : (
                            <p className="muted small">{t("この操作には変更の記録がありません。")}</p>
                          )}
                          {audit.before && audit.after && (
                            <details className="small">
                              <summary>{t("変更前後の全体")}</summary>
                              <dl className="kv">
                                <dt>{t("変更前の動画")}</dt>
                                <dd><Files dishId={dishId} paths={audit.before.videos} onOpen={onOpenMaterial} /></dd>
                                <dt>{t("変更後の動画")}</dt>
                                <dd><Files dishId={dishId} paths={audit.after.videos} onOpen={onOpenMaterial} /></dd>
                                <dt>{t("変更前の資料")}</dt>
                                <dd><Files dishId={dishId} paths={audit.before.docs} onOpen={onOpenMaterial} /></dd>
                                <dt>{t("変更後の資料")}</dt>
                                <dd><Files dishId={dishId} paths={audit.after.docs} onOpen={onOpenMaterial} /></dd>
                              </dl>
                            </details>
                          )}
                        </>
                      )}
                      {job && (
                        <dl className="kv">
                          <dt>{t("開始")}</dt>
                          <dd>{when(job.startedAt?.toDate() ?? null, locale)}</dd>
                          <dt>{t("終了")}</dt>
                          <dd>{when(job.finishedAt?.toDate() ?? null, locale)}</dd>
                          {job.runs != null && (
                            <>
                              <dt>{t("生成回数")}</dt>
                              <dd>{t("{n} 回で合議", { n: job.runs })}</dd>
                            </>
                          )}
                          {job.answerCount != null && (
                            <>
                              <dt>{t("反映した回答")}</dt>
                              <dd>{job.answerCount} {t("件")}</dd>
                            </>
                          )}
                          {job.approveApplied === false && (
                            <>
                              <dt>{t("承認")}</dt>
                              <dd className="error">
                                {t("適用されませんでした（未解消 {n} 件）", { n: job.remaining ?? 0 })}
                              </dd>
                            </>
                          )}
                          {job.error && (
                            <>
                              <dt>{t("エラー")}</dt>
                              <dd className="error">{job.error}</dd>
                            </>
                          )}
                        </dl>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
